import React, { useEffect, useState } from 'react';
import { useUser } from '@src/state/user/hooks';
import { event as adobeTaggingEvent } from '@uhg-abyss/web/tools/event';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Pagination, ResultCount } from '@uhg-abyss/web/ui/Pagination';
import { usePagination } from '@uhg-abyss/web/hooks/usePagination';
import { Card } from '@uhg-abyss/web/ui/Card';
import { Box } from '@uhg-abyss/web/ui/Box';
import { PageLayout } from '@src/common/PageLayout';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Button } from '@src/common/Button';
import { Loading } from '@src/common/Loading';

import {
  clearData,
  useTinRegistrationStatus,
} from '@src/state/tinRegistrationStatus';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { Accordion } from '@uhg-abyss/web/ui/Accordion';
import { TinRegistrationStatusHeader } from '../TinRegistrationStatus/TinRegistrationStatusHeader/TinRegistrationStatusHeader';
import { MultiTinRegistration } from './MultiTinRegistration/MultiTinRegistration';

const PageButtons = styled('div', {
  width: 'fitContent',
  marginTop: '3em',
  paddingBottom: '25px',
  fontWeight: 600,
  fontSize: '16px',
  color: '$primary1',
  fontFamily: '$heading',
  display: 'inline-flex',
  marginRight: '15px',
});

const CardTitle = styled('div', {
  padding: '0.7rem 0.5625rem 0.7rem 0.825rem',
  borderRadius: '0.1875rem 0.1875rem 0 0',
  color: '$gray8',
  display: 'flex',
  lineHeight: ({ theme }) => {
    return theme.fontSizes.size500;
  },
  fontSize: '17px',
  fontWeight: 'normal !important',
});

const HeaderTinStyle = styled('div', {
  fontWeight: 'bold',
  paddingLeft: '5px',
});

export const MultiTinRegistrationStatus = () => {
  const userInfo = useUser.state();
  const router = useRouter();
  const { clearRegistrationData } = clearData();
  const { searchRegistrationStatusSaga } = useTinRegistrationStatus.state();
  const statusInfo = useTinRegistrationStatus.state();
  const [accordionMap, setAccordionMap] = useState([]);
  const allRegistrations = statusInfo.multitinRegistration;
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [paginatedTinreistrations, setPaginatedTinreistrations] = useState([]);
  const [searchFilterCount, setSearchFilterCount] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const paginationProps = usePagination({ pages: pageCount });

  const setAccordion = (index) => {
    const values = [...accordionMap];
    values[index] = !values[index];
    setAccordionMap(values);
  };

  const goBack = () => {
    clearRegistrationData();
    router.navigate('/');
  };

  const gotoPage = (pageNumber) => {
    setCurrentPage(pageNumber + 1);
    const endRow = (pageNumber + 1) * pageSize;
    const startRow = endRow - pageSize;
    const paginatedRecords = allRegistrations.slice(startRow, endRow);
    setPaginatedTinreistrations(paginatedRecords);
  };

  const goPage = (pageNumber) => {
    setCurrentPage(pageNumber);
    const endRow = pageNumber * pageSize;
    const startRow = endRow - pageSize;
    const paginatedRecords = allRegistrations.slice(startRow, endRow);
    setPaginatedTinreistrations(paginatedRecords);
  };

  const form = useForm('');
  const searchCriteria = form.getValues('searchCriteria');

  useEffect(() => {
    const pageName = 'epvsecure:multitin registration status :results';
    const billingTin =
      searchCriteria && searchCriteria.tin ? searchCriteria.tin : '';
    const filterResults = allRegistrations.filter((item) => {
      return item;
    });
    const endRow = currentPage * pageSize;
    const startRow = endRow - pageSize;
    const registration = allRegistrations.slice(startRow, endRow);
    setPaginatedTinreistrations(registration);
    setPageSize(5);
    setSearchFilterCount(filterResults.length);
    setPageCount(Math.ceil(filterResults.length / pageSize));
    const siteSectionLevel = {
      siteSectionLevel1: 'multitin registration status',
      siteSectionLevel2: 'results',
      siteSectionLevel3: '',
      siteSectionLevel4: '',
      siteSectionLevel5: '',
    };
    adobeTaggingEvent('ON_PAGE_LOAD', {
      userInfo,
      pageName,
      billingTin,
      siteSectionLevel,
    });
  }, [allRegistrations]);

  return (
    <Loading
      isLoading={searchRegistrationStatusSaga.isLoading}
      style={{ height: '100%' }}
    >
      <PageLayout style={{ height: '100%' }}>
        <TinRegistrationStatusHeader />
        <React.Fragment>
          <Card>
            <Box color="$white">
              <div style={{ height: '5px' }} />
              <Accordion variant="custom" type="multiple">
                {paginatedTinreistrations.map((accordionItem, index) => {
                  return (
                    <Accordion.Item
                      value={paginatedTinreistrations[index].trackingNumber}
                    >
                      <Accordion.Trigger>
                        <Accordion.Header
                          onClick={() => {
                            return setAccordion(index);
                          }}
                          key={`ah${index}`}
                        >
                          <CardTitle id="Status-CardTitle">
                            Taxpayer Identification Number(TIN):{' '}
                            <HeaderTinStyle id="Status-CardTitle-tin">
                              {accordionItem.tin}
                            </HeaderTinStyle>
                            &nbsp;| Date Submitted:{' '}
                            <HeaderTinStyle id="Status-CardTitle-tin">
                              {accordionItem.submittedOn}
                            </HeaderTinStyle>
                          </CardTitle>
                        </Accordion.Header>
                      </Accordion.Trigger>
                      <Accordion.Content key={`acc${index}`}>
                        <MultiTinRegistration tinreg={accordionItem} />
                      </Accordion.Content>
                      <Accordion.Content />
                    </Accordion.Item>
                  );
                })}
              </Accordion>
              <div style={{ height: '5px' }} />
              <Flex justify="space-between">
                <ResultCount
                  {...paginationProps}
                  resultsTotalCount={searchFilterCount}
                  pageSize={5}
                  pageIndex={currentPage - 1}
                />
                <Pagination
                  {...paginationProps}
                  pageIndex={currentPage - 1}
                  gotoPage={gotoPage}
                  nextPage={() => {
                    return goPage(currentPage + 1);
                  }}
                  previousPage={() => {
                    return goPage(currentPage - 1);
                  }}
                  canPreviousPage={currentPage > 1}
                  canNextPage={currentPage < pageCount}
                />
              </Flex>
            </Box>
          </Card>
        </React.Fragment>
        <PageButtons>
          <Button
            type="button"
            id="status-back-button"
            aria-label="click here to go back"
            data-testid="back-button"
            onClick={goBack}
            width="100%"
          >
            Return to Dashboard
          </Button>
        </PageButtons>
      </PageLayout>
    </Loading>
  );
};
